// Rotating end-of-sentence phrase banks for drill prompts. Kept out of the
// React module so the selection logic can be unit-tested on its own.
//
// The nonsense problem this guards against: an `objectSlot` template ("{pronoun}
// {verb} {object}") used to pull from one generic object list, so "comer" could
// land "comer um livro" (eat a book). We reuse the `englishVerb` tag the
// templates already carry to look up a verb-appropriate object list instead.

import type { DrillTense } from './drillPool'

export type Phrase = { pt: string; en: string }
export type TimeFrame = 'present' | 'past' | 'imperfect' | 'future'
export type PhraseCategory = 'time' | 'location' | 'manner'

export const PHRASE_BANKS: {
  time: Record<TimeFrame, Phrase[]>
  location: Phrase[]
  manner: Phrase[]
  // Only reached for an objectSlot verb with no curated bank in OBJECT_BANKS.
  genericObject: Phrase[]
} = {
  time: {
    present: [
      { pt: 'hoje', en: 'today' },
      { pt: 'todos os dias', en: 'every day' },
      { pt: 'às vezes', en: 'sometimes' },
      { pt: 'sempre', en: 'always' },
    ],
    // Bounded moments in the past — what the preterite is for.
    past: [
      { pt: 'ontem', en: 'yesterday' },
      { pt: 'na semana passada', en: 'last week' },
      { pt: 'no ano passado', en: 'last year' },
    ],
    // The imperfect is the habitual / ongoing past ("we used to …"), so it takes
    // "back then" and "every day", not a one-off moment like "last week".
    imperfect: [
      { pt: 'antigamente', en: 'in the old days' },
      { pt: 'naquela época', en: 'back then' },
      { pt: 'todos os dias', en: 'every day' },
      { pt: 'sempre', en: 'always' },
      { pt: 'todo verão', en: 'every summer' },
    ],
    future: [
      { pt: 'amanhã', en: 'tomorrow' },
      { pt: 'na próxima semana', en: 'next week' },
      { pt: 'em junho', en: 'in June' },
    ],
  },
  location: [
    { pt: 'em casa', en: 'at home' },
    { pt: 'no parque', en: 'in the park' },
    { pt: 'na escola', en: 'at school' },
    { pt: 'no Brasil', en: 'in Brazil' },
  ],
  manner: [
    { pt: 'rápido', en: 'quickly' },
    { pt: 'bem', en: 'well' },
    { pt: 'devagar', en: 'slowly' },
    { pt: 'com cuidado', en: 'carefully' },
  ],
  genericObject: [
    { pt: 'isso', en: 'that' },
    { pt: 'uma coisa', en: 'something' },
  ],
}

// Object phrases paired to the verb (keyed by the template's `englishVerb`).
// Not a content-quality system — a plain lookup that keeps the objectSlot
// prompts from combining an unrelated verb with a random noun.
export const OBJECT_BANKS: Record<string, Phrase[]> = {
  hear: [
    { pt: 'um barulho', en: 'a noise' },
    { pt: 'a campainha', en: 'the doorbell' },
    { pt: 'uma voz', en: 'a voice' },
    { pt: 'a música', en: 'the music' },
  ],
  divide: [
    { pt: 'o bolo', en: 'the cake' },
    { pt: 'o trabalho', en: 'the work' },
    { pt: 'o quarto', en: 'the room' },
    { pt: 'a pizza', en: 'the pizza' },
  ],
  describe: [
    { pt: 'a cidade', en: 'the city' },
    { pt: 'a casa', en: 'the house' },
    { pt: 'o problema', en: 'the problem' },
    { pt: 'a viagem', en: 'the trip' },
  ],
  buy: [
    { pt: 'o pão', en: 'the bread' },
    { pt: 'um presente', en: 'a gift' },
    { pt: 'as frutas', en: 'some fruit' },
    { pt: 'um bilhete', en: 'a ticket' },
  ],
  bring: [
    { pt: 'o almoço', en: 'lunch' },
    { pt: 'uma cadeira', en: 'a chair' },
    { pt: 'os documentos', en: 'the documents' },
    { pt: 'a sobremesa', en: 'dessert' },
  ],
  help: [
    { pt: 'a professora', en: 'the teacher' },
    { pt: 'um amigo', en: 'a friend' },
    { pt: 'os alunos', en: 'the students' },
    { pt: 'a vizinha', en: 'the neighbour' },
  ],
  eat: [
    { pt: 'a pizza', en: 'the pizza' },
    { pt: 'o jantar', en: 'dinner' },
    { pt: 'uma maçã', en: 'an apple' },
    { pt: 'o almoço', en: 'lunch' },
  ],
  drink: [
    { pt: 'água', en: 'water' },
    { pt: 'um café', en: 'a coffee' },
    { pt: 'o suco', en: 'the juice' },
    { pt: 'chá', en: 'tea' },
  ],
  learn: [
    { pt: 'português', en: 'Portuguese' },
    { pt: 'a lição', en: 'the lesson' },
    { pt: 'uma música nova', en: 'a new song' },
    { pt: 'as regras', en: 'the rules' },
  ],
  sell: [
    { pt: 'a casa', en: 'the house' },
    { pt: 'o carro', en: 'the car' },
    { pt: 'o computador', en: 'the computer' },
    { pt: 'as frutas', en: 'the fruit' },
  ],
  write: [
    { pt: 'uma carta', en: 'a letter' },
    { pt: 'um e-mail', en: 'an email' },
    { pt: 'o relatório', en: 'the report' },
    { pt: 'uma história', en: 'a story' },
  ],
  understand: [
    { pt: 'a lição', en: 'the lesson' },
    { pt: 'a pergunta', en: 'the question' },
    { pt: 'o problema', en: 'the problem' },
    { pt: 'a explicação', en: 'the explanation' },
  ],
  open: [
    { pt: 'a porta', en: 'the door' },
    { pt: 'a janela', en: 'the window' },
    { pt: 'a caixa', en: 'the box' },
    { pt: 'a garrafa', en: 'the bottle' },
  ],
  watch: [
    { pt: 'o filme', en: 'the movie' },
    { pt: 'a série', en: 'the series' },
    { pt: 'o jogo', en: 'the game' },
    { pt: 'o noticiário', en: 'the news' },
  ],
  feel: [
    { pt: 'frio', en: 'cold' },
    { pt: 'calor', en: 'heat' },
    { pt: 'medo', en: 'fear' },
    { pt: 'dor', en: 'pain' },
  ],
  prefer: [
    { pt: 'o café', en: 'coffee' },
    { pt: 'o chá', en: 'tea' },
    { pt: 'o verão', en: 'summer' },
    { pt: 'a praia', en: 'the beach' },
  ],
  repeat: [
    { pt: 'a frase', en: 'the sentence' },
    { pt: 'a pergunta', en: 'the question' },
    { pt: 'a palavra', en: 'the word' },
    { pt: 'o exercício', en: 'the exercise' },
  ],
  serve: [
    { pt: 'o almoço', en: 'lunch' },
    { pt: 'o jantar', en: 'dinner' },
    { pt: 'o café', en: 'the coffee' },
    { pt: 'a sobremesa', en: 'dessert' },
  ],
  follow: [
    { pt: 'as regras', en: 'the rules' },
    { pt: 'o caminho', en: 'the path' },
    { pt: 'as instruções', en: 'the instructions' },
    { pt: 'o guia', en: 'the guide' },
  ],
  'ask for': [
    { pt: 'ajuda', en: 'help' },
    { pt: 'um favor', en: 'a favour' },
    { pt: 'a conta', en: 'the bill' },
    { pt: 'mais tempo', en: 'more time' },
  ],
  measure: [
    { pt: 'a mesa', en: 'the table' },
    { pt: 'a sala', en: 'the room' },
    { pt: 'a janela', en: 'the window' },
    { pt: 'a parede', en: 'the wall' },
  ],
  wear: [
    { pt: 'o casaco', en: 'the coat' },
    { pt: 'um vestido', en: 'a dress' },
    { pt: 'os sapatos', en: 'the shoes' },
    { pt: 'uma camisa', en: 'a shirt' },
  ],
  have: [
    { pt: 'tempo', en: 'time' },
    { pt: 'uma ideia', en: 'an idea' },
    { pt: 'razão', en: 'a point' },
    { pt: 'sorte', en: 'luck' },
  ],
  make: [
    { pt: 'o jantar', en: 'dinner' },
    { pt: 'um bolo', en: 'a cake' },
    { pt: 'planos', en: 'plans' },
    { pt: 'o trabalho', en: 'the work' },
  ],
  say: [
    { pt: 'a verdade', en: 'the truth' },
    { pt: 'o nome', en: 'the name' },
    { pt: 'uma palavra', en: 'a word' },
    { pt: 'adeus', en: 'goodbye' },
  ],
  give: [
    { pt: 'um presente', en: 'a gift' },
    { pt: 'uma resposta', en: 'an answer' },
    { pt: 'as chaves', en: 'the keys' },
    { pt: 'um conselho', en: 'some advice' },
  ],
  see: [
    { pt: 'o filme', en: 'the movie' },
    { pt: 'as fotos', en: 'the photos' },
    { pt: 'o resultado', en: 'the result' },
    { pt: 'a paisagem', en: 'the view' },
  ],
  know: [
    { pt: 'a resposta', en: 'the answer' },
    { pt: 'a verdade', en: 'the truth' },
    { pt: 'o caminho', en: 'the way' },
    { pt: 'a história', en: 'the story' },
  ],
  want: [
    { pt: 'um café', en: 'a coffee' },
    { pt: 'mais tempo', en: 'more time' },
    { pt: 'uma pausa', en: 'a break' },
    { pt: 'a resposta', en: 'the answer' },
  ],
  drive: [
    { pt: 'o carro', en: 'the car' },
    { pt: 'um caminhão', en: 'a truck' },
    { pt: 'o ônibus', en: 'the bus' },
    { pt: 'a moto', en: 'the motorcycle' },
  ],
  // Modal/incomplete-without-a-complement verbs (poder, conseguir "to
  // manage", insistir, decidir) — a bare verb + random time/location/manner
  // tail reads as broken Portuguese for these, so they get a real infinitive
  // complement instead, same mechanism as any other objectSlot verb.
  can: [
    // Not "help you" — the drilled subject can itself be "you" (tu/você),
    // which would read as "you can help you".
    { pt: 'ajudar em casa', en: 'help at home' },
    { pt: 'ir amanhã', en: 'go tomorrow' },
    { pt: 'chegar mais cedo', en: 'arrive earlier' },
    { pt: 'esperar um pouco', en: 'wait a bit' },
  ],
  manage: [
    { pt: 'terminar o trabalho', en: 'to finish the work' },
    { pt: 'chegar a tempo', en: 'to arrive on time' },
    { pt: 'resolver o problema', en: 'to solve the problem' },
    { pt: 'encontrar as chaves', en: 'to find the keys' },
  ],
  insist: [
    { pt: 'em ajudar', en: 'on helping' },
    { pt: 'em pagar', en: 'on paying' },
    { pt: 'em ir', en: 'on going' },
    { pt: 'em ficar', en: 'on staying' },
  ],
  decide: [
    { pt: 'ficar em casa', en: 'to stay home' },
    { pt: 'viajar sozinho', en: 'to travel alone' },
    { pt: 'estudar mais', en: 'to study more' },
    { pt: 'mudar de emprego', en: 'to change jobs' },
  ],
}

const TENSE_TIME_FRAME: Record<DrillTense, TimeFrame> = {
  present: 'present',
  preterite: 'past',
  imperfect: 'imperfect',
  presentSubjunctive: 'future',
  future: 'future',
}

export function shuffle<T>(values: T[]): T[] {
  const result = [...values]
  for (let index = result.length - 1; index > 0; index -= 1) {
    const other = Math.floor(Math.random() * (index + 1))
    ;[result[index], result[other]] = [result[other], result[index]]
  }
  return result
}

// Per-session picker. Each keyed bank is a shuffled queue, fully cycled before
// it repeats. `phrase` rotates Time (restricted to the tense's frame),
// Location, or Manner; `object` returns a verb-appropriate object.
export function createPhrasePicker() {
  const queues = new Map<string, Phrase[]>()

  function take(key: string, bank: Phrase[]): Phrase {
    let queue = queues.get(key)
    if (!queue || queue.length === 0) {
      queue = shuffle(bank)
      queues.set(key, queue)
    }
    return queue.pop() as Phrase
  }

  return {
    // `allowed` restricts which category(ies) can be drawn — a verb whose
    // fixedComplement already names a place ("fica em casa") or is a stative
    // state a manner adverb can't modify ("sabe a resposta rápido" is not
    // real Portuguese) passes a narrower list instead of all three.
    phrase(tense: DrillTense, allowed?: PhraseCategory[]): Phrase {
      const frame = TENSE_TIME_FRAME[tense]
      const options: Array<[string, PhraseCategory, Phrase[]]> = [
        [`time:${frame}`, 'time', PHRASE_BANKS.time[frame]],
        ['location', 'location', PHRASE_BANKS.location],
        ['manner', 'manner', PHRASE_BANKS.manner],
      ]
      const pool = allowed ? options.filter(([, category]) => allowed.includes(category)) : options
      const [key, , chosen] = pool[Math.floor(Math.random() * pool.length)]
      return take(key, chosen)
    },
    object(englishVerb: string): Phrase {
      const bank = OBJECT_BANKS[englishVerb]
      return bank ? take(`object:${englishVerb}`, bank) : take('object:generic', PHRASE_BANKS.genericObject)
    },
  }
}
